import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCheck, RefreshCw, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "../lib/api";
import { BRAND_COLORS, withAlpha, STATUS_COLORS } from "../lib/bookingStatus";
import { deDateMediumTime, deDateShortTime } from "../lib/dateFormat";

/**
 * Team · Impersonations-Audit
 * ---------------------------
 * Zeigt alle QR-Impersonations der letzten 30 Tage (wer hat wen wann
 * imitiert, wann konsumiert oder abgelaufen). Dient der DSGVO-konformen
 * Nachvollziehbarkeit — nur Admins dürfen die Liste sehen.
 */

const RELATIVE = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60)   return `vor ${diffSec} s`;
  if (diffSec < 3600) return `vor ${Math.round(diffSec / 60)} min`;
  if (diffSec < 86400) return `vor ${Math.round(diffSec / 3600)} h`;
  return deDateShortTime(d);
};

const ABSOLUTE = (iso) =>
  iso ? deDateMediumTime(iso) : "—";

function StateBadge({ state }) {
  // Grün · konsumiert (Team hat wirklich reingesehen)
  // Grau · abgelaufen (2 min ohne Scan)
  // Blau · noch aktiv (2-min-Fenster läuft, noch nicht konsumiert)
  const conf = {
    consumed: { bg: STATUS_COLORS.green, fg: BRAND_COLORS.ivory, label: "Konsumiert",  Icon: CheckCircle2 },
    active:   { bg: STATUS_COLORS.blue, fg: BRAND_COLORS.ivory, label: "Aktiv",       Icon: Clock },
    expired:  { bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, label: "Abgelaufen",  Icon: XCircle },
  }[state] || { bg: STATUS_COLORS.grey, fg: BRAND_COLORS.navy, label: state, Icon: XCircle };
  const Icon = conf.Icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] tracking-widest uppercase font-medium"
      style={{ background: conf.bg, color: conf.fg }}
      data-testid={`impersonation-state-${state}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2} />
      {conf.label}
    </span>
  );
}

export default function TeamImpersonations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/team/impersonations");
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Konnte Audit-Liste nicht laden.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const consumedCount = rows.filter((r) => r.state === "consumed").length;
  const activeCount   = rows.filter((r) => r.state === "active").length;
  const expiredCount  = rows.filter((r) => r.state === "expired").length;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="team-impersonations-page">
      <div className="flex items-center gap-3 mb-1">
        <UserCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
        <h1 className="text-2xl md:text-3xl font-serif text-navy">Kundenansicht · Audit</h1>
      </div>
      <p className="text-[13px] text-navy-dark/70 max-w-2xl leading-snug">
        Volle Nachvollziehbarkeit aller QR-Impersonationen: wer aus dem Team
        hat welche Kundenansicht wann geöffnet — inkl. Konsumierung und
        Ablauf. Die Liste umfasst die letzten 30 Tage.
      </p>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-3 gap-3 mt-6 mb-5" data-testid="impersonation-summary">
        <div className="bg-white p-4 shadow-sm" style={{ borderRadius: 8 }}>
          <div className="label-caps" style={{ color: STATUS_COLORS.green }}>Konsumiert</div>
          <div className="font-display text-3xl text-navy mt-1" data-testid="summary-consumed">{consumedCount}</div>
        </div>
        <div className="bg-white p-4 shadow-sm" style={{ borderRadius: 8 }}>
          <div className="label-caps" style={{ color: STATUS_COLORS.blue }}>Aktiv</div>
          <div className="font-display text-3xl text-navy mt-1" data-testid="summary-active">{activeCount}</div>
        </div>
        <div className="bg-white p-4 shadow-sm" style={{ borderRadius: 8 }}>
          <div className="label-caps text-navy-dark/60">Abgelaufen</div>
          <div className="font-display text-3xl text-navy mt-1" data-testid="summary-expired">{expiredCount}</div>
        </div>
      </div>

      <div className="flex items-center justify-end mb-3">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="impersonation-refresh"
          className="inline-flex items-center gap-2 text-navy px-3 py-1.5 text-[10.5px] tracking-[0.16em] uppercase border border-navy/25 hover:border-gold hover:text-gold-dark bg-white transition-colors disabled:opacity-40"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6} />}
          Aktualisieren
        </button>
      </div>

      {/* Liste */}
      {error && (
        <div className="bg-white p-4 border-l-4" style={{ borderColor: STATUS_COLORS.red, color: STATUS_COLORS.red }} data-testid="impersonation-error">
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="bg-white p-8 text-center" style={{ borderRadius: 8, boxShadow: `0 1px 3px ${withAlpha(BRAND_COLORS.navy, 0.05)}` }} data-testid="impersonation-empty">
          <UserCheck className="w-10 h-10 mx-auto mb-3 text-navy-dark/30" strokeWidth={1.3} />
          <div className="font-display text-lg text-navy mb-1">Noch keine Impersonation aufgezeichnet</div>
          <div className="text-[12px] text-navy-dark/60">
            Sobald ein Team-Mitglied im Kundenansicht-Modal einen QR-Code erzeugt, taucht der Eintrag hier auf.
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <ul className="space-y-2" data-testid="impersonation-list">
          {rows.map((r) => (
            <li
              key={r.id}
              className="bg-white p-4 flex items-center gap-4 flex-wrap md:flex-nowrap"
              style={{ borderRadius: 8, boxShadow: `0 1px 3px ${withAlpha(BRAND_COLORS.navy, 0.05)}` }}
              data-testid={`impersonation-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-0.5">
                  Team-Mitglied
                </div>
                <div className="font-medium text-navy truncate">{r.actor_name || r.actor_email || "—"}</div>
                <div className="text-[11px] text-navy-dark/55 truncate">{r.actor_email}</div>
              </div>
              <div className="hidden md:block text-navy-dark/25 text-2xl leading-none" aria-hidden>→</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/50 mb-0.5">
                  Kunde
                </div>
                <Link
                  to={`/team/customers/${r.customer_id}`}
                  className="font-medium text-navy hover:text-gold-dark truncate block"
                  data-testid={`impersonation-customer-link-${r.id}`}
                >
                  {r.customer_name || r.customer_email || "—"}
                </Link>
                <div className="text-[11px] text-navy-dark/55 truncate">{r.customer_email}</div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0 min-w-[130px]">
                <StateBadge state={r.state} />
                <div
                  className="text-[11px] text-navy-dark/60"
                  title={ABSOLUTE(r.created_at)}
                >
                  Erzeugt · {RELATIVE(r.created_at)}
                </div>
                {r.consumed_at && (
                  <div
                    className="text-[11px] font-medium"
                    style={{ color: STATUS_COLORS.green }}
                    title={ABSOLUTE(r.consumed_at)}
                  >
                    Konsumiert · {RELATIVE(r.consumed_at)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
